import { useEffect, useState } from "react";
import { fetchNotificacoesAtivas, type NotificacaoAtiva } from "../integration";
import { IconAlert, IconClose } from "../icons";

/** Chave no localStorage com os ids de avisos já dispensados pelo usuário. */
const DISMISSED_KEY = "sercamp-avisos-dispensados";
/** Intervalo de checagem dos avisos ativos no Portal. */
const POLL_MS = 60_000;

function readDismissed(): number[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
}

function persistDismissed(ids: number[]) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage indisponível (modo privado etc.) — dispensa só nesta sessão */
  }
}

/** Classe extra por tipo de aviso (a base .alert-banner já cobre "falha" em vermelho). */
const TIPO_CLASS: Partial<Record<NotificacaoAtiva["tipo"], string>> = {
  manutencao: "alert-banner-manutencao",
  comunicado: "alert-banner-comunicado",
};

/**
 * Banner de avisos transmitidos pelo Admin Cybersecurity via Portal (incidentes,
 * janelas de manutenção ou comunicados). Faz polling de um endpoint público e
 * somente leitura do Portal a cada 60s; cada aviso pode ser dispensado (some da
 * tela e fica marcado no localStorage para não reaparecer neste navegador).
 */
export default function AvisoSistema() {
  const [avisos, setAvisos] = useState<NotificacaoAtiva[]>([]);
  const [dismissed, setDismissed] = useState<number[]>(readDismissed);

  useEffect(() => {
    let cancelled = false;

    function load() {
      fetchNotificacoesAtivas()
        .then((lista) => {
          if (!cancelled) setAvisos(lista);
        })
        .catch(() => {
          // Falha de rede ao falar com o Portal: mantém o último estado
          // conhecido (não deve quebrar o app nem exibir erro para o usuário).
        });
    }

    load();
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  function dismiss(id: number) {
    setDismissed((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      persistDismissed(next);
      return next;
    });
  }

  const visiveis = avisos.filter((a) => !dismissed.includes(a.id));
  if (visiveis.length === 0) return null;

  return (
    <div className="aviso-sistema-stack">
      {visiveis.map((a) => (
        <div key={a.id} className={`alert-banner ${TIPO_CLASS[a.tipo] ?? ""}`}>
          <span className="alert-banner-ic">
            <IconAlert size={20} />
          </span>
          <div className="alert-banner-txt">
            <strong>{a.titulo}</strong>
            <span>{a.mensagem}</span>
          </div>
          <button
            type="button"
            className="alert-banner-close"
            onClick={() => dismiss(a.id)}
            title="Dispensar aviso"
            aria-label="Dispensar aviso"
          >
            <IconClose size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
